import { Group, Stack, Stepper, useMatches } from "@mantine/core";
import { schemaResolver, useForm } from "@mantine/form";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "./EditCarForm.module.css";
import EditStep1 from "./components/EditStep1";
import EditStep2 from "./components/EditStep2";
import EditStep3 from "./components/EditStep3";
import EditStep4 from "./components/EditStep4";
import { validateFormStepFields } from "./functions/EditCarForm.functions";

import { getCountryOptionValue } from "@/components/elements/UserOnBoarding/functions";
import { UpdateCarInput, editCarSchema } from "@/services/cars/update-car";
import { useProfile } from "@/services/profile/get-profile";

import type { FC } from "react";

export type EditCarFormProps = {
  onSubmit: (values: UpdateCarInput) => void;
  onCancel?: () => void;
  initialValues?: Partial<UpdateCarInput>;
  loading?: boolean;
};

export const EditCarForm: FC<EditCarFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const { data: user } = useProfile();

  const firstName = user?.additional_info?.first_name ?? user?.name ?? "";
  const lastName = user?.additional_info?.last_name ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  const form = useForm<UpdateCarInput>({
    initialValues: {
      nickname: "",
      owner: fullName,
      purchase_price: 0,
      purchase_date: dayjs(new Date()).format("YYYY-MM-DD"),
      licence_plate: "",
      color: "",
      interior_colors: "",
      condition: "",
      number_of_previous_owner: 0,
      special_equipment: "",
      vin: "",
      ...initialValues,
      country_of_registration: getCountryOptionValue(initialValues?.country_of_registration, t),
    },
    validate: schemaResolver(editCarSchema, { sync: true }),
    validateInputOnBlur: true,
  });

  const handleSubmit = (values: UpdateCarInput) => {
    onSubmit({
      ...values,
      purchase_date: dayjs(values.purchase_date).format("YYYY-MM-DD"),
      condition: values.condition || "",
    });
  };

  const isMobile = useMatches({ base: true, md: false });

  const WrapperForFields = isMobile ? Stack : Group;

  const wrapperProps = isMobile ? { gap: "md" } : { grow: true, align: "flex-start" };

  const stepFields: Record<number, (keyof UpdateCarInput)[]> = {
    0: [],
    1: ["owner", "purchase_price", "purchase_date"],
    2: [],
    3: [],
  };

  const isStepValid = validateFormStepFields(stepFields, form);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stepper
        active={activeStep}
        classNames={{
          steps: classes.steps,
          step: classes.step,
        }}
        onStepClick={(step) => {
          if (step <= activeStep) {
            setActiveStep(step);
          }
        }}
      >
        <Stepper.Step label={t("components.CreateCarForm.review")} py="lg">
          {/* Review is moved from step 4 to step 1 during edit. */}
          <EditStep4
            form={form}
            loading={loading}
            onCancel={onCancel}
            onSubmit={form.onSubmit(handleSubmit)}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            nextStep={() => setActiveStep((prev) => prev + 1)}
          />
        </Stepper.Step>

        <Stepper.Step label={t("components.CreateCarForm.basics")} py="lg">
          <EditStep1
            WrapperForFields={WrapperForFields}
            wrapperProps={wrapperProps}
            form={form}
            activeStep={activeStep}
            loading={loading}
            onCancel={onCancel}
            nextStep={() => setActiveStep((prev) => prev + 1)}
            isStepValid={isStepValid}
          />
        </Stepper.Step>

        <Stepper.Step label={t("components.CreateCarForm.carDetails")} py="lg">
          <EditStep2
            WrapperForFields={WrapperForFields}
            wrapperProps={wrapperProps}
            form={form}
            activeStep={activeStep}
            loading={loading}
            onCancel={onCancel}
            nextStep={() => setActiveStep((prev) => prev + 1)}
            showLessElements={true}
          />
        </Stepper.Step>

        <Stepper.Step label={t("components.CreateCarForm.extraDetails")} py="lg">
          <EditStep3
            WrapperForFields={WrapperForFields}
            wrapperProps={wrapperProps}
            form={form}
            activeStep={activeStep}
            loading={loading}
            onCancel={onCancel}
            onSubmit={form.onSubmit(handleSubmit)}
          />
        </Stepper.Step>
      </Stepper>
    </form>
  );
};
